/**
 * BranchScopeSwitcher — the "which branch am I looking at" control.
 *
 * In the header it sits beside the org name, so the top of every screen reads
 * *which entity · which branch · step back to my office*. It is the ONE home for
 * the reading lens: a screen that wants to know whose figures it is showing asks
 * `useBranchScope`, it does not grow a filter of its own. Two places to set the
 * same thing is how somebody ends up reading Tiger Cable's numbers under a header
 * that says everything.
 *
 * It renders only when there is something to say — more than one branch, or a
 * member pinned to one. A single-branch business sees nothing new, which is the
 * same rule the posting-form selectors follow.
 *
 * Two shapes, one control (mobileorg, 22 Aug 2026). The header pill is hidden
 * below 640px, where there is no room for it — which meant a phone could not see
 * or change the lens at all, and read whatever had last been chosen on a desktop
 * without saying so. `variant="panel"` is the same control as a full-width row for
 * the mobile navigation drawer, with its list inline rather than floating, because
 * a 288px drawer has nowhere to float to.
 *
 * @since brctx-1 (2026-08-19)
 */
import { useEffect, useRef, useState } from 'react';
import { Building, Check, ChevronDown, Lock } from 'lucide-react';
import { familyOrder } from '@/shared/branches/branchFamily';
import { useBranchScope, branchScopeLabel } from '@/shared/hooks/useBranchScope';

interface BranchScopeSwitcherProps {
    /**
     * `header` — the compact pill, from 640px up (the default).
     * `panel`  — a full-width row for the mobile drawer, always shown.
     */
    variant?: 'header' | 'panel';
}

export function BranchScopeSwitcher({ variant = 'header' }: BranchScopeSwitcherProps) {
    const branches = useBranchScope((s) => s.branches);
    const scopeId = useBranchScope((s) => s.scopeId);
    const locked = useBranchScope((s) => s.locked);
    const setScope = useBranchScope((s) => s.setScope);

    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const panel = variant === 'panel';

    useEffect(() => {
        function onClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
        if (open) {
            document.addEventListener('mousedown', onClick);
            document.addEventListener('keydown', onEsc);
        }
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onEsc);
        };
    }, [open]);

    // Nothing to choose between, and nothing worth the width.
    if (branches.length < 2 && !locked) return null;

    const label = branchScopeLabel(branches, scopeId);

    /**
     * A pinned member has no second option, so this is a statement rather than a
     * control — and it says WHY in visible text instead of a title attribute,
     * which a phone has no way to reveal.
     */
    if (locked) {
        return (
            <span
                className={`${panel ? 'mx-3 mb-2.5 flex w-[calc(100%-1.5rem)]' : 'hidden sm:flex'} items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800`}
            >
                <Lock size={11} className="shrink-0 text-stone-400" />
                <span className="truncate max-w-[160px]">{label}</span>
                <span className="text-[11px] text-stone-400">your only branch</span>
            </span>
        );
    }

    /** The choices themselves, identical in both shapes. */
    const options = (
        <>
            <p className="px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                Figures you are reading
            </p>

            <button
                onClick={() => { setScope(null); setOpen(false); }}
                className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
            >
                <Check size={14} className={`mt-0.5 shrink-0 ${scopeId ? 'opacity-0' : 'text-teal-600'}`} />
                <span className="min-w-0">
                    <span className="block text-[13px] text-stone-800 dark:text-stone-100">All branches</span>
                    {/* Naming them is the point: "all" asks the reader to
                        trust that the list in their head is the list Karr
                        used. */}
                    <span className="block text-[11px] text-stone-500 truncate">
                        Consolidated: {branches.map((b) => b.name).join(', ')}
                    </span>
                </span>
            </button>

            <div className="my-1 h-px bg-stone-100 dark:bg-stone-800" />

            {/* branchface-b: a branch inside another is drawn under it. */}
            {familyOrder(branches).map(({ row: b, depth }) => (
                <button
                    key={b.id}
                    onClick={() => { setScope(b.id); setOpen(false); }}
                    className={`w-full flex items-start gap-2 py-2 text-left hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors ${depth === 1 ? 'pl-7 pr-3' : 'px-3'}`}
                >
                    <Check size={14} className={`mt-0.5 shrink-0 ${scopeId === b.id ? 'text-teal-600' : 'opacity-0'}`} />
                    <span className="min-w-0">
                        <span className="block text-[13px] text-stone-800 dark:text-stone-100 truncate">{b.name}</span>
                        {b.gstin && (
                            <span className="block text-[11px] text-stone-500 font-mono truncate">{b.gstin}</span>
                        )}
                    </span>
                </button>
            ))}
        </>
    );

    return (
        <div ref={ref} className={panel ? 'relative mx-3 mb-2.5' : 'hidden sm:block relative'}>
            <button
                onClick={() => setOpen((o) => !o)}
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors ${panel ? 'w-full py-1.5' : ''} ${scopeId
                    ? 'text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/40 hover:bg-teal-100 dark:hover:bg-teal-900/40'
                    : 'text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700'}`}
                title="Which branch's figures you are reading"
            >
                <Building size={12} className="shrink-0" />
                <span className={`truncate ${panel ? 'flex-1 text-left' : 'max-w-[160px]'}`}>{label}</span>
                <ChevronDown size={12} className={`shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                panel ? (
                    // In the drawer the list opens IN the flow. Floating it would put
                    // it outside a 288px column with nowhere to go.
                    <div className="mt-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 py-1 max-h-[45vh] overflow-y-auto">
                        {options}
                    </div>
                ) : (
                    <div className="absolute left-0 mt-1.5 w-64 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 shadow-lg py-1 z-50">
                        {options}
                    </div>
                )
            )}
        </div>
    );
}
